import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock3, ShieldCheck, HandHeart } from "lucide-react";

const SKILLS = ["Mentorship", "Tutoring", "Cooking", "Event Setup", "Fundraising", "Photography", "Social Media", "Counseling", "Driving", "Handyman"];
const INTERESTS = ["Brotherhood", "Sisterhood", "Youth", "Family Restoration", "Events", "Admin & Ops"];

export default function Volunteer() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city: "",
    skills: [],
    interests: [],
    availability: "weekly",
    background_check_consent: false,
    prior_experience: "",
  });
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const toggle = (field, value) =>
    setForm((f) => ({ ...f, [field]: f[field].includes(value) ? f[field].filter((x) => x !== value) : [...f[field], value] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.background_check_consent) {
      toast.error("Background check consent is required to volunteer.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/volunteer-applications", form);
      toast.success("Volunteer application submitted. We'll follow up within 5 business days.");
      setOk(true);
    } catch (err) {
      toast.error("Could not submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="volunteer-page">
      <section className="section">
        <div className="container-editorial grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Volunteer portal</div>
            <h1 className="mt-4 font-serif text-5xl leading-[1.05] text-ink md:text-6xl">
              Show up for someone. <em className="not-italic text-clay">Consistently.</em>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink-muted">
              Our volunteers are the heartbeat of KEYZ — mentors, tutors, cooks, drivers, photographers and
              prayer partners. Fill out the application and we'll walk you through orientation, background check
              and cohort placement.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { Icon: ShieldCheck, title: "Background check", body: "Required for all volunteers. We cover the cost." },
                { Icon: Clock3, title: "Time commitment", body: "Weekly or monthly — you choose the rhythm that fits." },
                { Icon: HandHeart, title: "Hours tracking", body: "Log hours in your dashboard once approved." },
              ].map((x) => (
                <div key={x.title} className="flex items-start gap-4 rounded-2xl border border-clay/10 bg-white p-5">
                  <x.Icon className="mt-0.5 h-5 w-5 text-clay" />
                  <div>
                    <div className="font-serif text-lg text-ink">{x.title}</div>
                    <p className="text-sm text-ink-muted">{x.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            {ok ? (
              <div className="rounded-3xl border border-sage/30 bg-sage p-10 text-oat">
                <div className="font-serif text-3xl">Welcome, future volunteer.</div>
                <p className="mt-3">Your application is in. Watch your email for the orientation invite and background check link within 5 business days.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-clay/15 bg-white p-8 md:p-10">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="field-label">Full name *</label>
                    <input required data-testid="vol-full-name" className="field-input" value={form.full_name} onChange={set("full_name")} />
                  </div>
                  <div>
                    <label className="field-label">Email *</label>
                    <input required type="email" data-testid="vol-email" className="field-input" value={form.email} onChange={set("email")} />
                  </div>
                  <div>
                    <label className="field-label">Phone *</label>
                    <input required data-testid="vol-phone" className="field-input" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div>
                    <label className="field-label">City</label>
                    <input data-testid="vol-city" className="field-input" value={form.city} onChange={set("city")} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="field-label">Skills you'd offer</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {SKILLS.map((s) => (
                        <button
                          type="button"
                          key={s}
                          data-testid={`vol-skill-${s.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => toggle("skills", s)}
                          className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                            form.skills.includes(s)
                              ? "border-clay bg-clay text-oat"
                              : "border-clay/25 text-ink hover:border-clay hover:text-clay"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="field-label">Programs you'd like to serve</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {INTERESTS.map((s) => (
                        <button
                          type="button"
                          key={s}
                          data-testid={`vol-interest-${s.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => toggle("interests", s)}
                          className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                            form.interests.includes(s)
                              ? "border-sage bg-sage text-oat"
                              : "border-sage/30 text-ink hover:border-sage hover:text-sage"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="field-label">Availability</label>
                    <select data-testid="vol-availability" className="field-input" value={form.availability} onChange={set("availability")}>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every two weeks</option>
                      <option value="monthly">Monthly</option>
                      <option value="event_only">Events only</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Prior experience</label>
                    <input data-testid="vol-experience" className="field-input" value={form.prior_experience} onChange={set("prior_experience")} placeholder="Optional" />
                  </div>

                  <label className="sm:col-span-2 mt-2 flex items-start gap-3 text-sm text-ink-muted">
                    <input type="checkbox" data-testid="vol-consent" checked={form.background_check_consent} onChange={set("background_check_consent")} className="mt-1 h-4 w-4 accent-clay" required />
                    <span>I consent to a background check as required by KEYZ Foundation Inc.</span>
                  </label>

                  <div className="sm:col-span-2 mt-2 flex justify-end">
                    <button type="submit" disabled={loading} data-testid="vol-submit" className="btn-primary">
                      {loading ? "Submitting…" : "Submit Application"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
